import { Avatar, Box, Grid2, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';
import ProfileBubble from '@components/ProfileBubble';
import { Progress } from '@components/Progress';
import RoundedButton from '@components/RoundedButton';

import { MAX_DIRECT_UPLOAD_SIZE_MB } from '@constants/files';

import { useDeleteAvatar, useUploadFile } from '@hooks/api/file';
import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSnackbar } from '@hooks/useSnackbar';

import { formatInitials } from '@utils/format';

import { BrandIconInfo } from '@views/Organization/Setting/Brand';

import { useDeleteAccountAvatarMutation } from 'generated/file.generated';
import type { GetAccountSettingQuery } from 'generated/license.generated';
import type { AccountModel, UserModel } from 'generated/types';

const BASE_AVATAR_FONT_SIZE = 40;

const messages = defineMessages({
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
  delete: {
    id: 'GLOBAL.delete',
    defaultMessage: 'Delete',
  },
  deleteConfirm: {
    id: 'changeAvatar.deleteConfirm',
    defaultMessage: 'Are you sure you want to delete your avatar?',
  },
  deleteConfirmLogo: {
    id: 'changeAvatar.deleteConfirmLogo',
    defaultMessage: 'Are you sure you want to delete your brand icon?',
  },
  cancel: {
    id: 'GLOBAL.cancel',
    defaultMessage: 'Cancel',
  },
  uploadProfilePhoto: {
    id: 'changeAvatar.uploadProfilePhoto',
    defaultMessage: 'Choose File',
  },
  title: {
    id: 'changeAvatar.title',
    defaultMessage: 'Profile Photo',
  },
  success: {
    id: 'changeAvatar.success',
    defaultMessage: 'Successfully updated profile photo',
  },
  error: {
    id: 'changeAvatar.error',
    defaultMessage:
      'An error occurred while attempting to update profile photo',
  },
  fileSizeTitle: {
    id: 'image.fileSizeTitle',
    defaultMessage: '<b>Max File Size: </b>',
  },
  fileSizeValue: {
    id: 'image.fileSizeValue',
    defaultMessage: '{limit} MB',
  },
});

interface ChangeAvatarProps {
  user?: Pick<
    UserModel,
    'first_name' | 'last_name' | 'profile_picture' | 'user_id'
  >;
  account?: Pick<
    AccountModel,
    'account_id' | 'account_name' | 'profile_picture'
  >;
  accountSettings?: GetAccountSettingQuery;
  logo?: boolean;
  loading: boolean;
}

const ChangeAvatar: React.FC<ChangeAvatarProps> = ({
  user,
  account,
  accountSettings,
  logo = false,
  loading: userLoading,
}) => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [{ uploadAvatar }] = useUploadFile();
  const [deleteAvatar, deleteAvatarLoading] = useDeleteAvatar();
  const [deleteAccountAvatar, { loading: deleteAccountAvatarLoading }] =
    useDeleteAccountAvatarMutation();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
  });
  const [previewUrl, setPreviewUrl] = React.useState<string | undefined>(
    undefined
  );
  React.useEffect(() => {
    if (!file) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const loading =
    uploading || deleteAvatarLoading || deleteAccountAvatarLoading;

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    if (!user?.user_id && !account?.account_id) {
      return;
    }

    if (acceptedFiles.length) {
      setFile(acceptedFiles[0]);
    } else if (fileRejections.length > 0) {
      // File-level error
      fileRejections.forEach(({ errors, file }) => {
        const message = errors
          .map(({ message }) =>
            message.replace(/(\d+) bytes/gi, (bytes) => {
              const mb = (parseInt(bytes, 10) / 1_000_000).toFixed(2);
              return `${mb} MB`;
            })
          )
          .join('\n');

        enqueueSnackbar({
          message: `${file.name}: ${message}`,
          variant: 'error',
        });
      });
    }
  };

  const handleUpload = async () => {
    if (!file || (!user?.user_id && !account?.account_id) || loading) {
      return;
    }
    setUploading(true);
    const type = account?.account_id ? 'account' : 'user';

    await uploadAvatar(
      file,
      (user?.user_id || account?.account_id) ?? '',
      type,
      logo,
      (uploadProgress) => setProgress(uploadProgress * 100)
    );

    setUploading(false);
    setProgress(null);
    setFile(null);
  };

  const handleCancel = () => {
    setFile(null);
  };

  const handleDelete = async () => {
    if (
      (!user?.profile_picture && !account?.profile_picture && !logo) ||
      loading
    ) {
      return;
    }
    if (user?.user_id) {
      await deleteAvatar({
        input: {
          user_id: user.user_id,
        },
      });
    } else if (account?.account_id && logo) {
      const response = await deleteAccountAvatar({
        variables: {
          input: {
            account_id: account.account_id,
            fileType: 'logo',
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({
              __typename: 'AccountSettingModel',
              account_id: account.account_id,
            }),
            fields: {
              branding_logo_url: () => null,
            },
          });
        },
      });
      const { data, errors } = response;
      if (errors) {
        handleError(errors, 'deleteLogo');
      } else if (data?.deleteAccountAvatar) {
        handleSuccess('deleteLogo');
      }
    } else if (account?.account_id) {
      const response = await deleteAccountAvatar({
        variables: { input: { account_id: account.account_id } },
        update: (cache) => {
          cache.modify({
            id: cache.identify({
              __typename: 'AccountModel',
              account_id: account.account_id,
            }),
            fields: {
              profile_picture: () => null,
            },
          });
        },
      });

      const { data, errors } = response;
      if (errors) {
        handleError(errors, 'deleteAvatar');
      } else if (data?.deleteAccountAvatar) {
        handleSuccess('deleteAvatar');
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg'],
      'image/jpeg': ['.jpeg'],
      'image/gif': ['.gif'],
    },
    maxFiles: 1,
    maxSize: MAX_DIRECT_UPLOAD_SIZE_MB * 1000000,
    minSize: 1,
    onDrop: handleDrop,
    multiple: false,
  });

  const rootProps = getRootProps();
  const inputProps = getInputProps();

  const initials = account?.account_id
    ? formatInitials(account.account_name ?? undefined)
    : formatInitials(user?.first_name, user?.last_name);

  const handleChooseFile = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    (rootProps as unknown as any).ref.current.click();
  };

  const scale = 256 / BASE_AVATAR_FONT_SIZE;

  return (
    <Grid2 container spacing={2}>
      <Grid2
        container
        size={{ xs: 12 }}
        spacing={2}
        alignItems="flex-start"
        justifyContent="center"
      >
        {logo && (
          <Grid2 size={{ xs: 12, md: 6 }}>
            <BrandIconInfo />
          </Grid2>
        )}

        <Grid2 size={{ xs: 12, md: logo ? 6 : 12 }}>
          <Box
            sx={{
              ...(isDragActive && {
                backgroundColor: 'action.active',
                opacity: 0.5,
              }),
              '&:hover': {
                backgroundColor: 'action.hover',
                cursor: 'pointer',
                opacity: 0.5,
                borderRadius: '50%',
              },
              width: 'fit-content',
              margin: '0 auto',
            }}
            {...rootProps}
          >
            <input {...inputProps} />
            <div
              style={{
                border: '2px dashed #AAA',
                borderRadius: '50%',
              }}
            >
              {(user?.profile_picture ||
                account?.profile_picture ||
                accountSettings?.getAccountSetting?.branding_logo_url) &&
              !userLoading &&
              !file ? (
                <ProfileBubble
                  plain
                  s3Key={
                    logo
                      ? (accountSettings?.getAccountSetting
                          ?.branding_logo_url ?? '')
                      : (user?.profile_picture ??
                        account?.profile_picture ??
                        '')
                  }
                  size={logo ? 200 : 256}
                />
              ) : null}

              {userLoading ||
              (!user?.profile_picture &&
                !account?.profile_picture &&
                !accountSettings?.getAccountSetting?.branding_logo_url) ||
              file ? (
                <Avatar
                  src={previewUrl}
                  sx={{
                    width: logo ? 200 : 256,
                    height: logo ? 200 : 256,
                  }}
                >
                  {user || account ? (
                    <span style={{ transform: `scale(${scale})` }}>
                      {initials}
                    </span>
                  ) : (
                    <Icon
                      color="inherit"
                      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                      name={account ? 'users' : 'circle-user'}
                    />
                  )}
                </Avatar>
              ) : null}
            </div>
          </Box>
          {!logo && (
            <>
              <Typography variant="subtitle2" align="center">
                {formatMessage(messages.fileSizeTitle)}
                {formatMessage(messages.fileSizeValue, {
                  limit: MAX_DIRECT_UPLOAD_SIZE_MB,
                })}
              </Typography>
            </>
          )}
          {typeof progress === 'number' ? (
            <Progress value={progress} width="100%" />
          ) : null}
        </Grid2>
      </Grid2>

      <Grid2 size={{ xs: 12 }}>
        <RoundedButton onClick={handleChooseFile}>
          {formatMessage(messages.uploadProfilePhoto)}
        </RoundedButton>
      </Grid2>

      <Grid2 size={{ sm: 6, xs: 6 }}>
        <RoundedButton disabled={!file || loading} onClick={handleUpload}>
          {formatMessage(messages.save)}
        </RoundedButton>
      </Grid2>

      <Grid2 size={{ sm: 6, xs: 6 }}>
        {file ? (
          <RoundedButton
            disabled={loading}
            onClick={handleCancel}
            variant="red"
          >
            {formatMessage(messages.cancel)}
          </RoundedButton>
        ) : (
          <ConfirmationDialog
            onConfirm={handleDelete}
            title={
              logo
                ? formatMessage(messages.deleteConfirmLogo)
                : formatMessage(messages.deleteConfirm)
            }
          >
            {({ handleOpen }) => (
              <RoundedButton
                disabled={
                  loading ||
                  (logo
                    ? !accountSettings?.getAccountSetting?.branding_logo_url
                    : !user?.profile_picture && !account?.profile_picture)
                }
                onClick={handleOpen}
                variant="red"
              >
                {formatMessage(messages.delete)}
              </RoundedButton>
            )}
          </ConfirmationDialog>
        )}
      </Grid2>
    </Grid2>
  );
};

export default ChangeAvatar;
